/**
 * De labelfeed en het labeloverzicht (MA-61): wat de API teruggeeft over de
 * energielabelregistraties die MA-60 verzamelt.
 *
 * Een "rij" is één registratie van RVO in een buurt van het zoekgebied, met
 * wat wij er zelf aan toevoegen: de straat uit PDOK, de buurt, het vorige label
 * als dat in onze rijen staat, en de woning uit onze vangst als het adres daar
 * bekend is. Dat laatste is het hele punt: een vers label dat nog niet op Funda
 * staat, is een huis dat eraan komt.
 */
import { z } from 'zod';
import { energielabelSchema } from './energielabel';
import { LABELGROEPEN, type Labelgroep } from './zoekfilters';

/** Hoeveel dagen terug de feed standaard kijkt, en wat de knoppen bieden. */
export const LABELFEED_DAGEN_STANDAARD = 14;
export const LABELFEED_DAGEN_KEUZES = [7, 14, 30, 60] as const;
export const LABELFEED_DAGEN_MAX = 120;

/**
 * Welke klassen de feed toont: die van het zoekfilter (Labels-scherm, Kanban),
 * of alle klassen (de kaartlaag, waar de kleur van het pilletje al zegt wat het is).
 */
export const LABELFEED_KLASSEN = ['zoekfilter', 'alle'] as const;
export const labelfeedKlassenSchema = z.enum(LABELFEED_KLASSEN);
export type LabelfeedKlassen = z.infer<typeof labelfeedKlassenSchema>;

/** Grens van het m²-filter op de labellaag. */
export const LABELFEED_M2_MAX = 1000;

export const labelfeedQuerySchema = z.object({
  dagen: z.coerce.number().int().min(1).max(LABELFEED_DAGEN_MAX).default(LABELFEED_DAGEN_STANDAARD),
  klassen: labelfeedKlassenSchema.default('zoekfilter'),
  /**
   * Komma-gescheiden groepen (`D,E,F,G`) - de kaartlaag kiest zelf. Gaat voor
   * `klassen`; leeg of weggelaten laat `klassen` beslissen.
   */
  groepen: z
    .string()
    .optional()
    .transform((waarde) =>
      (waarde ?? '')
        .split(',')
        .map((deel) => deel.trim().toUpperCase())
        .filter((deel): deel is Labelgroep => (LABELGROEPEN as readonly string[]).includes(deel)),
    ),
  /** Oppervlakte volgens het label, in m², grenzen inbegrepen. */
  minM2: z.coerce.number().int().min(0).max(LABELFEED_M2_MAX).optional(),
  maxM2: z.coerce.number().int().min(0).max(LABELFEED_M2_MAX).optional(),
});
export type LabelfeedQuery = z.infer<typeof labelfeedQuerySchema>;

/** De woning uit onze eigen vangst die bij een registratie hoort. */
export const labelfeedWoningSchema = z.object({
  id: z.string().uuid(),
  adres: z.string(),
  status: z.string(),
  beoordeling: z.string(),
  teKoopSinds: z.string().nullable(),
  vraagprijs: z.number().nullable(),
});
export type LabelfeedWoning = z.infer<typeof labelfeedWoningSchema>;

export const labelfeedRijSchema = z.object({
  id: z.number().int(),
  /** De dag van het mutatiebestand waarin de registratie binnenkwam. */
  dag: z.string(),
  registratiedatum: z.string().nullable(),
  opnamedatum: z.string().nullable(),
  energieklasse: energielabelSchema.nullable(),
  /** Het label dat dit adres daarvoor had, als dat in onze rijen staat. */
  vorigeKlasse: energielabelSchema.nullable(),
  /** `Frans Halsstraat 66-2`, of `1072BT 66-2` zolang de straat onbekend is. */
  adres: z.string(),
  postcode: z.string(),
  buurtcode: z.string().nullable(),
  buurtnaam: z.string().nullable(),
  /** De CBS-wijk van die buurt: `WK0363EU`, `Helmersbuurt`. */
  wijkcode: z.string().nullable(),
  wijknaam: z.string().nullable(),
  oppervlakteM2: z.number().nullable(),
  bouwjaar: z.number().int().nullable(),
  gebouwtype: z.string().nullable(),
  certificaathouder: z.string().nullable(),
  /** Waar het op de kaart staat: het adres zelf, of anders het midden van de postcode. */
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  /** True als het punt van het adres is; false als het de postcode is. */
  puntPrecies: z.boolean(),
  woning: labelfeedWoningSchema.nullable(),
});
export type LabelfeedRij = z.infer<typeof labelfeedRijSchema>;

export const labelfeedSchema = z.object({
  dagen: z.number().int(),
  /** De klassen uit het zoekfilter waarop de feed is gefilterd. */
  klassen: z.array(energielabelSchema),
  /** De laatste dag waarvan een mutatiebestand is geladen; null als er nog niets ligt. */
  laatsteDag: z.string().nullable(),
  /** Hoeveel buurten het zoekgebied telt; 0 betekent dat alle buurten meetellen. */
  buurten: z.number().int(),
  rijen: z.array(labelfeedRijSchema),
});
export type Labelfeed = z.infer<typeof labelfeedSchema>;

export const labelBuurtTellingSchema = z.object({
  buurtcode: z.string(),
  buurtnaam: z.string(),
  /** Registraties van woningen (status Bestaand) in de periode. */
  registraties: z.number().int(),
  /** Daarvan in de klassen van het zoekfilter. */
  inFilter: z.number().int(),
  /** Daarvan met een woning in onze vangst die daarna te koop kwam. */
  teKoop: z.number().int(),
});
export type LabelBuurtTelling = z.infer<typeof labelBuurtTellingSchema>;

export const labelDagTellingSchema = z.object({
  dag: z.string(),
  registraties: z.number().int(),
  inFilter: z.number().int(),
  verwijderingen: z.number().int(),
});
export type LabelDagTelling = z.infer<typeof labelDagTellingSchema>;

export const labelConversieRijSchema = z.object({
  /** Eén klasse, of null voor de rij "alle". */
  klasse: energielabelSchema.nullable(),
  registraties: z.number().int(),
  teKoop: z.number().int(),
  /** `teKoop / registraties`, of null bij nul registraties. */
  aandeel: z.number().nullable(),
});
export type LabelConversieRij = z.infer<typeof labelConversieRijSchema>;

export const labelOverzichtSchema = z.object({
  /** De periode van de tellingen per buurt en per dag, in dagen terug vanaf vandaag. */
  dagen: z.number().int(),
  klassen: z.array(energielabelSchema),
  laatsteDag: z.string().nullable(),
  buurten: z.number().int(),
  perBuurt: z.array(labelBuurtTellingSchema),
  perDag: z.array(labelDagTellingSchema),
  conversie: z.object({
    /** Registraties met een registratiedatum in dit venster; oud genoeg om op Funda te staan. */
    vanaf: z.string(),
    totEnMet: z.string(),
    rijen: z.array(labelConversieRijSchema),
  }),
});
export type LabelOverzicht = z.infer<typeof labelOverzichtSchema>;
